import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  History,
  ChevronDown,
  ChevronUp,
  Calendar,
  RefreshCw,
  PlusCircle,
  MinusCircle,
  FileText
} from 'lucide-react';
import agtEduService from '../services/agtEduService';

const DocumentHistoryPanel = ({ documentId, colors }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (documentId) {
      fetchDocumentHistory(documentId);
    }
  }, [documentId]);

  const fetchDocumentHistory = async (docId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agtEduService.getDocumentEditHistory(docId);
      
      if (response.success && response.data) {
        // Ordenar por fecha (más recientes primero)
        const sortedHistory = response.data.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        
        setHistory(sortedHistory);
      } else {
        setError('No se pudo cargar el historial de ediciones');
      }
    } catch (err) {
      console.error('Error al cargar historial de ediciones:', err);
      setError('Error al cargar el historial de ediciones');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'replace':
        return <RefreshCw size={16} />;
      case 'insert':
        return <PlusCircle size={16} />;
      case 'delete':
        return <MinusCircle size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  // Si no hay historial pero tenemos datos simulados
  const simulatedHistory = [
    {
      id: 'edit-1',
      timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hora atrás
      message: 'Corregir errores gramaticales en la introducción',
      changes: {
        documentType: 'word',
        changes: [
          {
            type: 'replace',
            oldContent: 'Este documento contine errores',
            content: 'Este documento contiene errores',
            location: { paragraph: 1 }
          },
          {
            type: 'replace',
            oldContent: 'que debemos correjir',
            content: 'que debemos corregir',
            location: { paragraph: 1 }
          }
        ]
      }
    },
    {
      id: 'edit-2',
      timestamp: new Date(Date.now() - 7200000).toISOString(), // 2 horas atrás
      message: 'Añadir sección de conclusiones',
      changes: {
        documentType: 'word',
        changes: [
          {
            type: 'insert',
            content: 'Conclusiones: En este trabajo hemos analizado los principales factores...',
            location: { paragraph: 10 }
          }
        ]
      }
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: colors.textDefault }}>
        Historial de Ediciones
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={30} />
        </Box>
      ) : error ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : history.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {history.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              sx={{
                mb: 2,
                overflow: 'hidden',
                backgroundColor: colors.backgroundSecondary,
                borderRadius: 2
              }}
            >
              <ListItem
                button
                onClick={() => toggleExpand(item.id)}
                sx={{
                  px: 2,
                  py: 1.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <History size={20} color={colors.accent} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 500, color: colors.textDefault }}>
                      {item.message || 'Edición de documento'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Calendar size={14} style={{ marginRight: 4, color: colors.textSecondary }} />
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {formatDate(item.timestamp)}
                      </Typography>
                      {item.changes?.changes && (
                        <Chip
                          label={`${item.changes.changes.length} cambios`}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            backgroundColor: `${colors.accent}20`,
                            color: colors.accent
                          }}
                        />
                      )}
                    </Box>
                  }
                />
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item.id);
                  }}
                >
                  {expandedItems[item.id] ? (
                    <ChevronUp size={20} color={colors.textSecondary} />
                  ) : (
                    <ChevronDown size={20} color={colors.textSecondary} />
                  )}
                </IconButton>
              </ListItem>
              
              <Collapse in={expandedItems[item.id]} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, py: 1.5, backgroundColor: colors.cardBackground }}>
                  {item.changes?.changes ? (
                    <List dense>
                      {item.changes.changes.map((change, idx) => (
                        <ListItem key={idx} sx={{ px: 1, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getChangeTypeIcon(change.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                {change.type === 'replace' ? (
                                  <Typography variant="body2">
                                    Reemplazado "{change.oldContent || '...'}" por "{change.content || '...'}"
                                  </Typography>
                                ) : change.type === 'insert' ? (
                                  <Typography variant="body2">
                                    Insertado: "{change.content || '...'}"
                                  </Typography>
                                ) : change.type === 'delete' ? (
                                  <Typography variant="body2">
                                    Eliminado: "{change.content || '...'}"
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">
                                    {change.type}: {change.content || '...'}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              change.location ? (
                                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                  {change.location.paragraph
                                    ? `Párrafo ${change.location.paragraph}`
                                    : change.location.cell
                                    ? `Celda ${change.location.cell}${
                                        change.location.sheet ? ` (Hoja: ${change.location.sheet})` : ''
                                      }`
                                    : change.location.slide
                                    ? `Diapositiva ${change.location.slide}`
                                    : change.location.page
                                    ? `Página ${change.location.page}`
                                    : change.location.line
                                    ? `Línea ${change.location.line}`
                                    : ''}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      No hay detalles disponibles para esta edición
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </List>
      ) : (
        // Si no hay historial real, mostrar datos simulados
        <List sx={{ width: '100%' }}>
          {simulatedHistory.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              sx={{
                mb: 2,
                overflow: 'hidden',
                backgroundColor: colors.backgroundSecondary,
                borderRadius: 2
              }}
            >
              <ListItem
                button
                onClick={() => toggleExpand(item.id)}
                sx={{
                  px: 2,
                  py: 1.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <History size={20} color={colors.accent} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 500, color: colors.textDefault }}>
                      {item.message || 'Edición de documento'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Calendar size={14} style={{ marginRight: 4, color: colors.textSecondary }} />
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {formatDate(item.timestamp)}
                      </Typography>
                      {item.changes?.changes && (
                        <Chip
                          label={`${item.changes.changes.length} cambios`}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            backgroundColor: `${colors.accent}20`,
                            color: colors.accent
                          }}
                        />
                      )}
                    </Box>
                  }
                />
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item.id);
                  }}
                >
                  {expandedItems[item.id] ? (
                    <ChevronUp size={20} color={colors.textSecondary} />
                  ) : (
                    <ChevronDown size={20} color={colors.textSecondary} />
                  )}
                </IconButton>
              </ListItem>
              
              <Collapse in={expandedItems[item.id]} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, py: 1.5, backgroundColor: colors.cardBackground }}>
                  {item.changes?.changes ? (
                    <List dense>
                      {item.changes.changes.map((change, idx) => (
                        <ListItem key={idx} sx={{ px: 1, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getChangeTypeIcon(change.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                {change.type === 'replace' ? (
                                  <Typography variant="body2">
                                    Reemplazado "{change.oldContent || '...'}" por "{change.content || '...'}"
                                  </Typography>
                                ) : change.type === 'insert' ? (
                                  <Typography variant="body2">
                                    Insertado: "{change.content || '...'}"
                                  </Typography>
                                ) : change.type === 'delete' ? (
                                  <Typography variant="body2">
                                    Eliminado: "{change.content || '...'}"
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">
                                    {change.type}: {change.content || '...'}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              change.location ? (
                                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                  {change.location.paragraph
                                    ? `Párrafo ${change.location.paragraph}`
                                    : change.location.cell
                                    ? `Celda ${change.location.cell}${
                                        change.location.sheet ? ` (Hoja: ${change.location.sheet})` : ''
                                      }`
                                    : change.location.slide
                                    ? `Diapositiva ${change.location.slide}`
                                    : change.location.page
                                    ? `Página ${change.location.page}`
                                    : change.location.line
                                    ? `Línea ${change.location.line}`
                                    : ''}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      No hay detalles disponibles para esta edición
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );
};

export default DocumentHistoryPanel;